import { Injectable } from "@angular/core";
import { map, Observable } from "rxjs";
import { HttpParams } from "@angular/common/http";
import { ServerDataSource } from "angular2-smart-table";
import { environment } from "../../../environments/environment";
import { HttpService } from "./common/http.service";
import { User } from "../interfaces/auth/users";
import { omit } from "lodash";
import { defaultDataOmitFields, prepRecordForApi } from "../utils/utils.service";
import { Appraisal, AppraisalData } from "../interfaces/performance";
import { UserStore } from "../stores/user.store";

@Injectable()
export class AppraisalService extends AppraisalData {

  private readonly url: string;

  get gridDataSource(): ServerDataSource {
    return this.api.getServerDataSource(`${environment.API_BASE_URL}/${this.url}?populate=*`);
  }

  get gridArchivedDataSource(): ServerDataSource {
    return this.api.getServerDataSource(`${environment.API_BASE_URL}/${this.url}?filters[archivedOn][$notNull]=true`);
  }

  constructor(
    private api: HttpService,
    private userStore: UserStore
  ) {
    super();
    this.url = 'Appraisals';
  }

  getDataSourceOfClient(clientId: number): ServerDataSource {
    return this.api.getServerDataSource(`${environment.API_BASE_URL}/${this.url}?populate=*&clients_include_id=${clientId}`);
  }

  listAll(): Observable<Appraisal[]> {
    let params: HttpParams = new HttpParams()
      .append('limit', '-1')
      .append('offset', '0')
      .append('with', 'deep,2');

    return this.api.get(this.url, { params }).pipe(map(res => res.data || res));
  }

  list(pageNumber: number, pageSize: number, query: string = ''): Observable<Appraisal[]> {
    let params: HttpParams = new HttpParams()
      .append('with', 'deep,2');

    if (pageSize > 0) {
      params = params.append('limit', `${pageSize}`);
    }

    if (pageNumber > 0) {
      params = params.append('offset', `${(pageNumber - 1) * pageSize}`);
    }

    if (query) {
      params = params.append('where', query);
    }

    return this.api.get(this.url, { params }).pipe(map(res => res.data || res));
  }

  rawQry(pageNumber: number, pageSize: number, params: HttpParams):Observable<Appraisal[] | any> {
    if (pageSize > 0) {
      params = params.append('limit', `${pageSize}`);
    }

    if (pageNumber > 0) {
      params = params.append('offset', `${(pageNumber - 1) * pageSize}`);
    }

    // 'with' for population, 'where' for filters, 'order' for sorting

    return this.api.get(this.url, { params }).pipe(map(res => res.data || res));
  }

  get(id: any): Observable<Appraisal> {
    return this.api.get(`${this.url}/${id}`, { params: new HttpParams().append('populate', '*') })
      .pipe(map(res => res.data || res));
  }

  create(appraisal: Appraisal): Observable<Appraisal> {
    let params: HttpParams = new HttpParams()
      .append('populate', '*');

    // appraisal.reviewer = this.userStore.getUser().documentId;
    appraisal = prepRecordForApi(omit(appraisal, ['Personnel']));
    return this.api.post(this.url, { data: appraisal }, params)
      .pipe(map(res => res.data));
  }

  update(appraisal: Appraisal): Observable<Appraisal> {
    let params: HttpParams = new HttpParams()
      .append('populate', '*');
    
    const changedAppraisal = prepRecordForApi(omit(appraisal, [...defaultDataOmitFields, 'Personnel']));
    return this.api.put(`${this.url}/${appraisal.documentId}`, { data: changedAppraisal }, params)
      .pipe(map(res => res.data));
  }

  archive(Appraisal: Appraisal, date: Date, user: User): Observable<Appraisal> {
    return this.api.put(`${this.url}/${Appraisal.documentId}`, {
      data: {
        archivedOn: date,
        archivedBy: user,
      },
    });
  }

  unarchive(Appraisal: Appraisal): Observable<Appraisal> {
    return this.api.put(`${this.url}/${Appraisal.documentId}`, {
      data: {
        archivedOn: null,
        archivedBy: null,
      },
    });
  }

  delete(id: any): Observable<any> {
    return this.api.delete(`${this.url}/${id}`);
  }

}